<script setup>
import { computed, ref, onMounted, nextTick, getCurrentInstance } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
const props = defineProps({
  modelValue: { type: [String, Number], default: '' },
  type: { type: String, default: 'text' },
  customInputClass: { type: [String, Object, Array], default: '' },
  placeholder: { type: String, default: '' },
  label: { type: String, default: '' },
  hint: { type: String, default: '' },
  id: { type: String, default: '' },
  size: {
    type: String,
    default: 'md',
    validator: value => ['sm', 'md'].includes(value),
  },
  message: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  readonly: { type: Boolean, default: false },
  messageType: {
    type: String,
    default: 'info',
    validator: value => ['info', 'error', 'success'].includes(value),
  },
  min: { type: String, default: '' },
  max: { type: String, default: '' },
  autofocus: { type: Boolean, default: false },
});

const emit = defineEmits([
  'update:modelValue',
  'blur',
  'input',
  'focus',
  'enter',
]);

const hintSymbol = '?';

// Generate a unique ID per component instance when `id` prop is not provided.
const { uid } = getCurrentInstance();
const uniqueId = computed(() => props.id || `input-${uid}`);

const isFocused = ref(false);
const inputRef = ref(null);
const nativeDateInputRef = ref(null);

const isDateInput = computed(() => props.type === 'date');

const formattedDateValue = computed(() => {
  if (!isDateInput.value || !props.modelValue) return '';

  const rawValue = String(props.modelValue);
  const match = rawValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return rawValue;

  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  const isValidDate =
    date.getFullYear() === Number(year) &&
    date.getMonth() === Number(month) - 1 &&
    date.getDate() === Number(day);

  if (!isValidDate) return rawValue;

  const configuredLocale =
    typeof window !== 'undefined'
      ? window.chatwootConfig?.selectedLocale
      : undefined;
  const documentLocale =
    typeof document !== 'undefined' ? document.documentElement.lang : '';
  const locale = (configuredLocale || documentLocale || undefined)?.replace(
    '_',
    '-'
  );

  return new Intl.DateTimeFormat(locale).format(date);
});

const messageClass = computed(() => {
  switch (props.messageType) {
    case 'error':
      return 'text-n-ruby-9 dark:text-n-ruby-9';
    case 'success':
      return 'text-n-teal-10 dark:text-n-teal-10';
    default:
      return 'text-n-slate-11 dark:text-n-slate-11';
  }
});

const inputOutlineClass = computed(() => {
  switch (props.messageType) {
    case 'error':
      return 'outline-n-ruby-8 dark:outline-n-ruby-8 hover:outline-n-ruby-9 dark:hover:outline-n-ruby-9 disabled:outline-n-ruby-8 dark:disabled:outline-n-ruby-8';
    default:
      return 'outline-n-weak dark:outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 disabled:outline-n-weak dark:disabled:outline-n-weak focus:outline-n-brand dark:focus:outline-n-brand';
  }
});

const handleInput = event => {
  let value = event.target.value;
  // Convert to number if type is number and value is not empty
  if (props.type === 'number' && value !== '') {
    value = Number(value);
  }
  emit('update:modelValue', value);
  emit('input', event);
};

const handleFocus = event => {
  emit('focus', event);
  isFocused.value = true;
};

const sizeClass = computed(() => {
  switch (props.size) {
    case 'sm':
      return 'h-8 !px-3 !py-2';
    case 'md':
      return 'h-10 !px-3 !py-2.5';
    default:
      return 'h-10 !px-3 !py-2.5';
  }
});

const handleBlur = event => {
  emit('blur', event);
  isFocused.value = false;
};

const handleEnter = event => {
  emit('enter', event);
};

const openDatePicker = () => {
  if (props.disabled || props.readonly) return;

  const nativeDateInput = nativeDateInputRef.value;
  if (!nativeDateInput) return;

  try {
    if (typeof nativeDateInput.showPicker === 'function') {
      nativeDateInput.showPicker();
      return;
    }
  } catch {
    // Browsers may reject showPicker in restricted contexts. The native click
    // below preserves the available fallback without changing the field value.
  }

  nativeDateInput.click();
};

onMounted(() => {
  if (props.autofocus) {
    nextTick(() => {
      inputRef.value?.focus();
    });
  }
});
</script>

<template>
  <div class="relative flex flex-col min-w-0 gap-1">
    <div v-if="label" class="mb-0.5 flex min-h-5 items-center gap-1">
      <label :for="uniqueId" class="text-heading-3 text-n-slate-12">
        {{ label }}
      </label>
      <button
        v-if="hint"
        v-tooltip.top="{
          content: hint,
          delay: { show: 150, hide: 0 },
        }"
        type="button"
        class="inline-flex size-4 shrink-0 cursor-help items-center justify-center rounded-full border border-n-strong text-[10px] font-semibold leading-none text-n-slate-11 transition-colors hover:border-n-slate-8 hover:bg-n-alpha-2 hover:text-n-slate-12 focus:outline-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-n-brand"
        :aria-label="hint"
        :title="hint"
      >
        <span aria-hidden="true">{{ hintSymbol }}</span>
      </button>
    </div>
    <!-- Added prefix slot to allow adding icons to the input -->
    <slot name="prefix" />
    <div v-if="isDateInput" class="relative min-w-0">
      <input
        :id="uniqueId"
        v-bind="$attrs"
        ref="inputRef"
        :value="formattedDateValue"
        :class="[
          customInputClass,
          inputOutlineClass,
          sizeClass,
          {
            error: messageType === 'error',
            focus: isFocused,
          },
        ]"
        type="text"
        :placeholder="placeholder || $t('DATE_PICKER.PLACEHOLDER')"
        :disabled="disabled"
        readonly
        class="block w-full reset-base text-sm !mb-0 !pe-10 outline outline-1 border-none border-0 outline-offset-[-1px] rounded-lg bg-n-alpha-black2 text-ellipsis placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out"
        @focus="handleFocus"
        @blur="handleBlur"
        @keyup.enter="handleEnter"
      />
      <button
        type="button"
        class="absolute inset-y-0 end-0 inline-flex w-10 items-center justify-center text-n-slate-11 transition-colors hover:text-n-brand focus:outline-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-n-brand disabled:cursor-not-allowed disabled:opacity-50"
        :aria-label="$t('DATE_PICKER.OPEN')"
        :disabled="disabled || readonly"
        @click="openDatePicker"
      >
        <Icon icon="i-lucide-calendar-days" class="size-4" />
      </button>
      <input
        ref="nativeDateInputRef"
        :value="modelValue"
        type="date"
        tabindex="-1"
        aria-hidden="true"
        class="pointer-events-none absolute bottom-0 end-0 size-px opacity-0"
        :disabled="disabled"
        :readonly="readonly"
        :min="min || undefined"
        :max="max || undefined"
        @input="handleInput"
      />
    </div>
    <input
      v-else
      :id="uniqueId"
      v-bind="$attrs"
      ref="inputRef"
      :value="modelValue"
      :class="[
        customInputClass,
        inputOutlineClass,
        sizeClass,
        {
          error: messageType === 'error',
          focus: isFocused,
        },
      ]"
      :type="type"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readonly"
      :min="['date', 'datetime-local', 'time'].includes(type) ? min : undefined"
      :max="
        ['date', 'datetime-local', 'time', 'number'].includes(type)
          ? max
          : undefined
      "
      class="block w-full reset-base text-sm !mb-0 outline outline-1 border-none border-0 outline-offset-[-1px] rounded-lg bg-n-alpha-black2 file:border-0 file:bg-transparent file:text-sm file:font-medium text-ellipsis placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
      @input="handleInput"
      @focus="handleFocus"
      @blur="handleBlur"
      @keyup.enter="handleEnter"
    />
    <p
      v-if="message"
      class="min-w-0 mt-1 mb-0 text-label-small leading-4 transition-all duration-500 ease-in-out"
      :class="messageClass"
    >
      {{ message }}
    </p>
  </div>
</template>
